<script setup lang="ts">
import { AuthState } from '@/stores/auth-store'
import type { ActionType } from '@/types/action-type'
import AuthStateOverlayContent from './AuthStateOverlayContent.vue'

const authStore = useAuthStore()

const { authState } = storeToRefs(authStore)

const router = useRouter()
const toast = useToast()
const { t } = useI18n()

interface AuthStateRenderOptions {
  title: string
  message: string
  action?: ActionType
}

const renderOptions = computed((): AuthStateRenderOptions => {
  switch (authState.value) {
    case AuthState.Initializing:
      return {
        title: t('auth.state.initializing.title'),
        message: t('auth.state.initializing.message'),
      }
    case AuthState.ExpiredToken:
      return {
        title: t('auth.state.expiredToken.title'),
        message: t('auth.state.expiredToken.message'),
        action: {
          label: t('auth.state.expiredToken.action'),
          action: async () => {
            const success = await authStore.requestRefreshToken()
            if (!success) {
              toast.error(t('auth.state.expiredToken.error'))
              await authStore.logout()
            } else {
              toast.success(t('auth.state.expiredToken.success'))
            }

            await router.push({
              name: '/login',
            })
          },
        },
      }
    case AuthState.InvalidToken:
      return {
        title: t('auth.state.invalidToken.title'),
        message: t('auth.state.invalidToken.message'),
        action: {
          label: t('auth.state.invalidToken.action'),
          action: async () => {
            if (!await authStore.isAuthenticated()) {
              return
            }

            await authStore.logout()
          },
        },
      }
    case AuthState.UnexpectedError:
      return {
        title: t('auth.state.unexpectedError.title'),
        message: t('auth.state.unexpectedError.message'),
        action: {
          label: t('auth.state.unexpectedError.action'),
          action: async () => {
            await router.push({
              name: '/',
            })
          },
        },
      }
    case AuthState.AuthenticationError:
      return {
        title: t('auth.state.authenticationError.title'),
        message: t('auth.state.authenticationError.message'),
        action: {
          label: t('auth.state.authenticationError.action'),
          action: async () => {
            await router.push({
              name: '/login',
            })
          },
        },
      }
    case AuthState.ServerNotReachable:
      return {
        title: t('auth.state.serverNotReachable.title'),
        message: t('auth.state.serverNotReachable.message'),
        action: {
          label: t('auth.state.serverNotReachable.action'),
          action: async () => {
            router.go(0)
          },
        },
      }

    default:
      return {
        title: '',
        message: '',
      }
  }
})

const isProcessing = ref(false)

async function handleAction() {
  const action = renderOptions.value.action?.action
  if (!action) {
    return
  }

  isProcessing.value = true
  await action()
  isProcessing.value = false
}
</script>

<template>
  <AuthStateOverlayContent
    :title="renderOptions.title"
    :message="renderOptions.message"
    :action-label="renderOptions.action?.label"
    :show-progress="authState === AuthState.Initializing"
    :is-processing="isProcessing"
    @action="handleAction"
  />
</template>
